/**
 * Zone Form Component
 * Create/edit zone form
 */

import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, Input } from '../ui';
import { Save, X } from 'lucide-react';
import type { Zone } from '../../types';

// Predefined colors for zones
const ZONE_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
];

export interface ZoneFormData {
  name: string;
  color: string;
  coordinates: number[][][];
}

interface ZoneFormProps {
  zone?: Zone | null;
  coordinates?: number[][][];
  onSave: (data: ZoneFormData) => void;
  onCancel: () => void;
  isSaving?: boolean;
  isDrawingMode?: boolean;
}

export default function ZoneForm({
  zone,
  coordinates,
  onSave,
  onCancel,
  isSaving,
  isDrawingMode = false,
}: ZoneFormProps) {
  const randomZoneColor = () => ZONE_COLORS[Math.floor(Math.random() * ZONE_COLORS.length)];

  const [name, setName] = useState(zone?.name || '');
  const [color, setColor] = useState(() => zone?.color || randomZoneColor());

  // Reset the form when the zone prop changes (identity check, as the old effect had)
  const [prevZone, setPrevZone] = useState(zone);
  if (zone !== prevZone) {
    setPrevZone(zone);
    setName(zone?.name || '');
    setColor(zone ? zone.color : randomZoneColor());
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const finalCoordinates = coordinates || zone?.polygon.coordinates;
    
    if (!name.trim()) {
      alert('Please enter a zone name');
      return;
    }
    
    if (!finalCoordinates || finalCoordinates.length === 0) {
      alert('Please draw a zone polygon on the map');
      return;
    }

    onSave({
      name: name.trim(),
      color,
      coordinates: finalCoordinates,
    });
  };

  const isEditing = !!zone;
  const hasCoordinates = (coordinates && coordinates.length > 0) || 
                         (zone?.polygon.coordinates && zone.polygon.coordinates.length > 0);
  
  // When editing, require user to confirm polygon changes before allowing save
  const isPolygonConfirmed = isEditing ? (coordinates && coordinates.length > 0) : hasCoordinates;
  const canSave = !isDrawingMode && isPolygonConfirmed;

  return (
    <Card padding="none">
      <CardHeader className="px-4 pt-4">
        <CardTitle>{isEditing ? 'Edit Zone' : 'New Zone'}</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Zone Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Zone Name
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter zone name"
              required
            />
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zone Color
            </label>
            <div className="flex flex-wrap gap-2">
              {ZONE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-lg transition-all ${
                    color === c
                      ? 'ring-2 ring-offset-2 ring-gray-400 scale-110'
                      : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          {/* Polygon Status */}
          <div className={`p-3 rounded-lg ${isDrawingMode ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
            <div className="flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  isDrawingMode ? 'bg-amber-500 animate-pulse' : (isPolygonConfirmed ? 'bg-green-500' : 'bg-yellow-500')
                }`}
              />
              <span className={`text-sm ${isDrawingMode ? 'text-amber-700 font-medium' : 'text-gray-700'}`}>
                {isDrawingMode
                  ? 'Click "Confirm" on the map to save polygon changes'
                  : isPolygonConfirmed
                    ? 'Polygon confirmed'
                    : 'Draw polygon on map to define zone area'}
              </span>
            </div>
            {hasCoordinates && !isDrawingMode && (
              <p className="text-xs text-gray-500 mt-1 ml-5">
                {(coordinates || zone?.polygon.coordinates)?.[0]?.length || 0} points
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              type="submit"
              variant="primary"
              loading={isSaving}
              disabled={!canSave}
              leftIcon={<Save className="h-4 w-4" />}
              className="flex-1"
              title={!canSave ? 'Please confirm polygon changes on the map first' : undefined}
            >
              {isEditing ? 'Update Zone' : 'Create Zone'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              leftIcon={<X className="h-4 w-4" />}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
